import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  SunIcon,
  MoonIcon,
  ArrowRightOnRectangleIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import { OCRApiService } from '../services/api';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useFiles } from '../contexts/FilesContext';
import FloatingFeedbackButton from './FloatingFeedbackButton';
import toast from 'react-hot-toast';

// Stub for backward compatibility — old pages (HomePage) imported useQueue from Layout.
// Queue state now lives in FilesContext (activeJobIds) + useJobPolling hook.
export const useQueue = () => ({
  openQueue: (_jobIds: number[]) => {},
  isQueueOpen: false,
  submittedJobIds: [] as number[],
});

const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [apiHealthy, setApiHealthy] = useState<boolean | null>(null);
  const { theme, toggleTheme } = useTheme();
  const { user, logout, isAdmin } = useAuth();
  const { setSelectedFileId, resetAll } = useFiles();
  const navigate = useNavigate();

  const handleLogout = () => {
    // Capture everything we need from the current user BEFORE logout()
    // clears it, so we can decide where to send them next.
    const isSsoUser = !localStorage.getItem('auth_token');
    const keynetBaseUrl = (user?.account?.settings as { keynet_base_url?: string } | undefined)
      ?.keynet_base_url?.replace(/\/+$/, '');

    resetAll();
    logout();
    toast.success('Logged out');

    // Steve's ask (2026-10-06): the KeyScan tab was opened from KeyNet, so
    // Logout should close that tab and leave the user back on their KeyNet
    // tab. Only works when the browser allows window.close() (it will when
    // the tab was opened via a link/form from another origin, which is our
    // SSO case). If the close is blocked, fall back to a redirect — to
    // KeyNet for SSO users (so they're back where they came from), to the
    // login page for break-glass password users.
    window.close();
    // Give the browser a tick to actually close before we fall back.
    window.setTimeout(() => {
      if (window.closed) return;
      if (isSsoUser && keynetBaseUrl) {
        window.location.href = keynetBaseUrl;
      } else {
        navigate('/login');
      }
    }, 150);
  };

  useEffect(() => {
    const checkHealth = async () => {
      try {
        await OCRApiService.checkHealth();
        setApiHealthy(true);
      } catch {
        setApiHealthy(false);
      }
    };

    checkHealth();
    const interval = setInterval(checkHealth, 30000); // Check every 30s
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-200">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shadow-sm">
        <div className="px-6">
          <div className="flex items-center justify-between h-14">
            {/* Logo and Title */}
            <button
              onClick={() => { setSelectedFileId(null); navigate('/'); }}
              className="flex items-center cursor-pointer"
            >
              <img
                src="/logo.png"
                alt="KeyScan AP"
                className="h-9 w-9 mr-3"
              />
              <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
                KeyScan AP
              </h1>
            </button>

            {/* Right Side Controls */}
            <div className="flex items-center gap-3">
              {/* API Status — just a dot, no label needed when online */}
              <div
                className={`h-2 w-2 rounded-full ${
                  apiHealthy === null
                    ? 'bg-gray-400'
                    : apiHealthy
                    ? 'bg-green-500'
                    : 'bg-red-500 animate-pulse'
                }`}
                title={apiHealthy === null ? 'Checking API...' : apiHealthy ? 'API Online' : 'API Offline'}
              />

              {/* Theme Toggle — just an icon */}
              <button
                onClick={toggleTheme}
                className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
              >
                {theme === 'light' ? (
                  <MoonIcon className="h-4 w-4 text-gray-400" />
                ) : (
                  <SunIcon className="h-4 w-4 text-gray-400" />
                )}
              </button>

              {/* Divider */}
              <div className="h-5 w-px bg-gray-200 dark:bg-gray-700" />

              {/* User + Admin + Exit/Logout grouped. "Exit" for SSO users
                  since the button also closes their KeyNet-launched tab;
                  "Logout" for break-glass users where it's a normal session
                  end with no tab to close. */}
              <div className="flex items-center gap-1">
                {user && (
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300 mr-1">
                    {user.username}
                  </span>
                )}
                {isAdmin && (
                  <Link
                    to="/admin"
                    className="px-2.5 py-1 rounded-md bg-brand-50 dark:bg-brand-900/30 text-brand-600 dark:text-brand-400 text-xs font-semibold hover:bg-brand-100 dark:hover:bg-brand-900/50 transition-colors"
                  >
                    Admin
                  </Link>
                )}
                {(() => {
                  const isSsoUser = !!user && user.username.startsWith('keynet:');
                  const label = isSsoUser ? 'Exit' : 'Logout';
                  // SSO users: visible outlined button so clerks can find
                  // their way out of the KeyNet-launched tab. Break-glass
                  // users keep the quieter styling since Logout is used
                  // less often during active admin work.
                  const styleClass = isSsoUser
                    ? 'px-3 py-1 rounded-md text-xs font-semibold border border-brand-600 text-brand-600 dark:text-brand-400 dark:border-brand-500 hover:bg-brand-50 dark:hover:bg-brand-900/20 transition-colors'
                    : 'px-2.5 py-1 rounded-md text-xs font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors';
                  return (
                    <button
                      onClick={handleLogout}
                      className={styleClass}
                      aria-label={label}
                    >
                      {label}
                    </button>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content — full width, no max-w constraint */}
      <main className="relative">
        {children}
      </main>

      {/* Floating feedback button — visible on every page when logged in */}
      {user && <FloatingFeedbackButton />}
    </div>
  );
};

export default Layout;
